import React, { useState } from 'react';
import './styles/App.css';
import { PageLayout } from './components/PageLayout';
import { updateSavedField, readSelections } from './runLogStorage';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { RunLog } from './components/RunLog';
import { Button, Container, Row, Col, Form } from 'react-bootstrap';
import { v4 as uuid } from 'uuid';
import Select from 'react-select';
import { useMsal } from "@azure/msal-react";


function ErrorComponent({ error }) {
    return <p>An Error Occurred: {error}</p>;
}

function LoadingComponent() {
    return <p>Authentication in progress...</p>;
}

/**
 * If a user is authenticated the ProfileContent component above is rendered. Otherwise a message indicating a user is not authenticated is rendered.
 */
const MainContent = () => {
    const { accounts } = useMsal();

    const [runLogIdsList, setRunLogIds] = useState(() => {
        var rawLogIds = localStorage.getItem("runLogIds")
        return !!rawLogIds ? rawLogIds.split(',') : []
    });

    const [minColumnWidth, setMinColumnWidth] = useState(() => {
        return localStorage.getItem("storedColWidth") || 425;
    });

    const [numColumns, setNumColumns] = useState(() => {
        return localStorage.getItem("storedColCount") || "";
    });

    const outingsSettingsOptions = [
        { value: 'Driver', label: 'Driver Notes' },
        { value: 'Change', label: 'Change Notes' },
        { value: 'Tire', label: 'Tire Info' },
        { value: 'Lap', label: 'Lap Info' },
        { value: 'Weather', label: 'Weather Info' },
        { value: 'Changes', label: 'Setup Changes' }
    ]
    const [outingsListSettings, setOutingsListSettings] = useState([outingsSettingsOptions[0].value]);

    const [selectedEvent, setSelectedEvent] = useState("");
    const [EventsList, setEventsList] = useState(null);
    const [selectedSession, setSelectedSession] = useState("");
    const [SessionsList, setSessionsList] = useState(null);
    const [reverseOutingOrder, setReverseOutingOrder] = useState(false);


    const Events = EventsList && EventsList.map((Event) =>
        <option key={Event.eventId}>
            {Event.name}
        </option>);

    const Sessions = SessionsList && SessionsList.map((Session) =>
        <option key={Session.sessionId}>
            {Session.name}
        </option>);

    const defaultSelections = () => {
        var storedSelections = localStorage.getItem("storedSelections");

        if (storedSelections) {
            var splitSelections = storedSelections.split(',')
            setOutingsListSettings(splitSelections)
            return outingsSettingsOptions.filter(x => splitSelections.includes(x.value))
        } else {
            return outingsSettingsOptions[0];
        }
    }

    function AddRunLog() {
        var newId = uuid();

        //Set the local storage of the new Id to the same values as the old one to copy over any settings
        if (runLogIdsList && runLogIdsList.length - 1 > -1) {
            var prevRunLogId = runLogIdsList[runLogIdsList.length - 1]
            if (prevRunLogId) {
                // Copy via a guarded read so a missing/corrupt previous entry
                // yields {} instead of the literal string "null".
                localStorage.setItem(newId, JSON.stringify(readSelections(prevRunLogId)))
            }

        }
        var newIds = [...runLogIdsList, newId];

        localStorage.setItem("runLogIds", newIds.join(','))
        setRunLogIds(newIds);
    }

    const DeleteRunLog = (id) => {
        var newIds = runLogIdsList.filter(x => x !== id);
        localStorage.setItem("runLogIds", newIds.join(','));
        localStorage.removeItem(id);
        setRunLogIds(newIds);
    }

    //Callback function for updating the selected and available top level selections
    const UpdateTopLevelSelections = (event, eventsList, session, sessionsList, reverseOutings) => {

        if (event) {
            setSelectedEvent(event);
        }

        if (eventsList) {
            setEventsList(eventsList);
        }

        if (session) {
            setSelectedSession(session);
        }

        if (sessionsList) {
            setSessionsList(sessionsList);
        }

        if (reverseOutings) {
            setReverseOutingOrder(reverseOutings); //TODO: Make this happen on uncheck too? 
        }
    }

    const SettingsCheckChange = event => {
        if (event !== undefined) {
            const selectedEvents = event.map(event => event.value)
            localStorage.setItem("storedSelections", selectedEvents)
            setOutingsListSettings(selectedEvents)
        }
    }

    const EventChange = event => {
        if (event.target.value !== undefined && event.target.value !== "Events") {
            setSelectedEvent(event.target.value);

            //Update these values in all of the stored values for each run log
            runLogIdsList.forEach(element => updateSavedField(element, "Event", event.target.value));

        }
    }

    const SessionChange = event => {
        if (event.target.value !== undefined && event.target.value !== "Sessions") {
            setSelectedSession(event.target.value);

            //Update these values in all of the stored values for each run log
            runLogIdsList.forEach(element => updateSavedField(element, "Session", event.target.value));
        }
    }

    
    const ReverseCheckChange = event => {
        if (event.target.checked !== undefined) {
            setReverseOutingOrder(event.target.checked);

            //Update these values in all of the stored values for each run log
            runLogIdsList.forEach(element => updateSavedField(element, "reverseOutingOrder", event.target.checked));
        }
    }


    function RunLogsList() {
        // A single CSS grid keeps every panel keyed by its UUID at a stable
        // position, so a layout reflow can't re-parent panels and bleed one
        // panel's data into another. An explicit column count wins; otherwise
        // auto-fill lays out as many min-width columns as fit -- and because it
        // is auto-fill (not auto-fit) a partial last row keeps its column width
        // instead of stretching the leftover panels to full width.
        const fixedCols = parseInt(numColumns, 10);
        const minW = parseInt(minColumnWidth, 10) || 425;
        const gridTemplateColumns = fixedCols > 0
            ? `repeat(${fixedCols}, minmax(0, 1fr))`
            : `repeat(auto-fill, minmax(${minW}px, 1fr))`;

        return (
            <div className="runLogGrid" style={{ gridTemplateColumns }}>
                {runLogIdsList.map((runLogId, i) => (
                    <div className="runLog" key={runLogId}>
                        <RunLog
                            id={runLogId}
                            deleteFunction={DeleteRunLog}
                            outingsListSettings={outingsListSettings}
                            savedSelections={localStorage.getItem(runLogId)}
                            updateTopLevelSelections={i === 0 ? UpdateTopLevelSelections : null}
                        />
                    </div>
                ))}
            </div>
        );
    }

    const ALLOWED_DOMAIN = "trackhouse.com";
    const isTrackhouse = () => {
        // UX gate only — real authorization is enforced by the RVG API.
        // Anchor to the account's email domain so guest/#EXT# accounts and
        // lookalike addresses can't pass a loose substring match.
        const upn = accounts[0]?.username ?? "";
        return upn.split("@").pop()?.toLowerCase() === ALLOWED_DOMAIN;
    }


    const MinColumnWidthTextChange = event => {
        if (event.target.value !== undefined) {
            setMinColumnWidth(event.target.value)
            localStorage.setItem("storedColWidth", event.target.value)
        }
    }

    const NumColumnsTextChange = event => {
        if (event.target.value !== undefined) {
            setNumColumns(event.target.value)
            localStorage.setItem("storedColCount", event.target.value)
        }
    }

    function refreshPage() {
        window.location.reload(false);
    }

    return (
        <MsalAuthenticationTemplate
            // interactionType={InteractionType.Redirect}
            errorComponent={ErrorComponent}
            loadingComponent={LoadingComponent} >
            {isTrackhouse() && <Container fluid>
                <Row style={{ display: 'flex', marginBottom: '8px', marginTop: '-24px' }}>
                    <Col xs={1}>
                        <Button onClick={AddRunLog}>Add Run Log</Button>
                    </Col>
                    <Col xs={2}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', justifyContent: 'start', alignItems: 'center', columnGap: 4, rowGap: 2, fontSize: 10 }}>
                            <span style={{ textAlign: 'right' }}>Columns:</span>
                            <input style={{ fontSize: 12, width: 40 }} value={numColumns} type="text" onChange={NumColumnsTextChange} placeholder='auto' />
                            <span style={{ textAlign: 'right' }}>Min Width:</span>
                            <input style={{ fontSize: 12, width: 40 }} value={minColumnWidth} type="text" onChange={MinColumnWidthTextChange} placeholder='425' />
                        </div>
                    </Col>
                    <Col xs={3}>
                        <Row>
                            <Col>
                                <Form.Select className="runLog-button" value={selectedEvent} onChange={EventChange} size="sm">
                                    <option>Events</option>
                                    {Events}
                                </Form.Select>
                                <Form.Select className="runLog-button" value={selectedSession} onChange={SessionChange} size="sm">
                                    <option>Sessions</option>
                                    {Sessions}
                                </Form.Select>
                            </Col>
                            <Col>
                                <Button onClick={refreshPage}>Apply</Button>
                                <Form.Check className="runLogCheck" type="checkbox"style={{ width: 110 }} label="Reverse Outings" onChange={ReverseCheckChange} checked={reverseOutingOrder} />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={6}>
                        <Select styles={{
                            control: (baseStyles, state) => ({
                                ...baseStyles,
                                backgroundColor: 'slategray',
                                borderColor: 'slategray'
                            }),
                            menuList: (baseStyles, state) => ({
                                ...baseStyles,
                                backgroundColor: 'slategray',
                                color: 'black'

                            }),
                            multiValue: (baseStyles) => ({
                                ...baseStyles,
                                backgroundColor: '#e6e6e6'
                            }),
                            multiValueLabel: (baseStyles) => ({
                                ...baseStyles,
                                color: 'black'
                            }),
                            // Make the remove "x" clearly visible (react-select's
                            // default is a faint gray that disappears on the dark theme).
                            multiValueRemove: (baseStyles) => ({
                                ...baseStyles,
                                color: 'black',
                                ':hover': {
                                    backgroundColor: '#c0392b',
                                    color: 'white'
                                }
                            })
                        }} isMulti defaultValue={defaultSelections} closeMenuOnSelect={false} onChange={SettingsCheckChange} options={outingsSettingsOptions} />
                    </Col>
                </Row>
                {RunLogsList()}
            </Container>}
        </MsalAuthenticationTemplate>
    );
};

export default function App() {
    return (
        <PageLayout>
            <MainContent />
        </PageLayout>
    );
}